// Theatre cases: how a case moves, the safety checklist order, and theatre slots
// (docs/plans/phase2-theatre.md §4, §5).
//
//   booked ──sign_in──▶ in_theatre ──sign_out──▶ completed
//   booked ──cancel (reason)──▶ cancelled
//   booked ──reschedule──▶ booked
//
// After sign in a case can't be cancelled (T5, provisional): an operation stopped after sign
// in is completed, and the operation note says so.

import { err, ok, type Result } from '../result.ts';

export const THEATRE_CASE_STATUSES = ['booked', 'in_theatre', 'completed', 'cancelled'] as const;
export type TheatreCaseStatus = (typeof THEATRE_CASE_STATUSES)[number];

export const THEATRE_URGENCIES = ['elective', 'emergency'] as const;
export type TheatreUrgency = (typeof THEATRE_URGENCIES)[number];

export type TheatreCaseAction = 'reschedule' | 'sign_in' | 'sign_out' | 'cancel';

const TRANSITIONS: Record<TheatreCaseAction, [TheatreCaseStatus, TheatreCaseStatus]> = {
  reschedule: ['booked', 'booked'],
  sign_in: ['booked', 'in_theatre'],
  sign_out: ['in_theatre', 'completed'],
  cancel: ['booked', 'cancelled'],
};

export function theatreCaseTransition(
  from: TheatreCaseStatus,
  action: TheatreCaseAction,
): Result<TheatreCaseStatus, 'theatre.case_state'> {
  const [needed, to] = TRANSITIONS[action];
  return from === needed ? ok(to) : err('theatre.case_state');
}

// --- The checklist ---------------------------------------------------------------------------

/** In the order they are recorded. The wording of each phase's items is configuration (T1). */
export const CHECKLIST_PHASES = ['pre_op', 'sign_in', 'time_out', 'sign_out'] as const;
export type ChecklistPhase = (typeof CHECKLIST_PHASES)[number];

export const CHECKLIST_ANSWERS = ['yes', 'no', 'not_applicable'] as const;
export type ChecklistAnswer = (typeof CHECKLIST_ANSWERS)[number];

/** The case state each phase is recorded in (the sign-in record moves the case in). */
const PHASE_CASE_STATUS: Record<ChecklistPhase, TheatreCaseStatus> = {
  pre_op: 'booked',
  sign_in: 'booked',
  time_out: 'in_theatre',
  sign_out: 'in_theatre',
};

export type ChecklistPhaseErrorCode =
  | 'checklist.case_state'
  | 'checklist.already_recorded'
  | 'checklist.out_of_order'
  | 'checklist.time_before_previous';

/**
 * Whether `phase` may be recorded now: the case is in the phase's state, the phase isn't
 * recorded yet, every earlier phase is, and its time is not before the previous phase's.
 */
export function checklistPhaseCheck(input: {
  status: TheatreCaseStatus;
  recorded: readonly { phase: ChecklistPhase; at: Date }[];
  phase: ChecklistPhase;
  at: Date;
}): Result<ChecklistPhase, ChecklistPhaseErrorCode> {
  if (input.status !== PHASE_CASE_STATUS[input.phase]) return err('checklist.case_state');
  if (input.recorded.some((r) => r.phase === input.phase)) return err('checklist.already_recorded');
  const index = CHECKLIST_PHASES.indexOf(input.phase);
  const earlier = CHECKLIST_PHASES.slice(0, index);
  if (!earlier.every((p) => input.recorded.some((r) => r.phase === p))) {
    return err('checklist.out_of_order');
  }
  const previous = earlier.at(-1);
  const previousAt = input.recorded.find((r) => r.phase === previous)?.at;
  if (previousAt !== undefined && input.at.getTime() < previousAt.getTime()) {
    return err('checklist.time_before_previous');
  }
  return ok(input.phase);
}

/** An active checklist item as the rules need it. */
export interface ChecklistItemRule {
  id: string;
  /** An answer other than "Yes" stops the phase (T3: set by the clinical lead). */
  requiresYes: boolean;
}

export interface ChecklistAnswerInput {
  itemId: string;
  answer: ChecklistAnswer;
  note: string | null;
}

export type ChecklistAnswerErrorCode =
  | 'checklist.answer_missing'
  | 'checklist.answer_repeated'
  | 'checklist.item_unknown'
  | 'checklist.note_required'
  | 'checklist.requires_yes';

export type ChecklistAnswersResult =
  { ok: true; value: undefined } | { ok: false; code: ChecklistAnswerErrorCode; itemId: string };

const blank = (text: string | null) => text === null || text.trim() === '';

/**
 * Every active item answered once; nothing else. A "No" needs a note (T2, provisional,
 * TODO(clinical-verify)); an item that must be "Yes" stops the phase otherwise.
 */
export function checkChecklistAnswers(
  items: readonly ChecklistItemRule[],
  answers: readonly ChecklistAnswerInput[],
): ChecklistAnswersResult {
  const seen = new Set<string>();
  for (const a of answers) {
    if (seen.has(a.itemId))
      return { ok: false, code: 'checklist.answer_repeated', itemId: a.itemId };
    seen.add(a.itemId);
    const item = items.find((i) => i.id === a.itemId);
    if (item === undefined) return { ok: false, code: 'checklist.item_unknown', itemId: a.itemId };
    if (item.requiresYes && a.answer !== 'yes') {
      return { ok: false, code: 'checklist.requires_yes', itemId: a.itemId };
    }
    if (a.answer === 'no' && blank(a.note)) {
      return { ok: false, code: 'checklist.note_required', itemId: a.itemId };
    }
  }
  const missing = items.find((i) => !seen.has(i.id));
  if (missing !== undefined)
    return { ok: false, code: 'checklist.answer_missing', itemId: missing.id };
  return { ok: true, value: undefined };
}

// --- Slots -------------------------------------------------------------------------------------

export function caseEnd(start: Date, minutes: number): Date {
  return new Date(start.getTime() + minutes * 60_000);
}

/** Whether two slots share any time; one may start the moment another ends. */
export function slotsOverlap(
  a: { start: Date; minutes: number },
  b: { start: Date; minutes: number },
): boolean {
  return (
    a.start.getTime() < caseEnd(b.start, b.minutes).getTime() &&
    b.start.getTime() < caseEnd(a.start, a.minutes).getTime()
  );
}
